import React, { useState, useEffect } from 'react';
import { Card, Button, Collapse, Form, Spinner } from 'react-bootstrap';
import SubtleBadge from 'components/common/SubtleBadge';
import classNames from 'classnames';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import DatePicker from 'react-datepicker';
import { IoFilter } from 'react-icons/io5';
import { MdOutlineFilterListOff } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';

function DatepickerFiltre({ startDate, endDate, onChange }) {
  return (
    <DatePicker
      popperPlacement="bottom-start"
      selected={startDate}
      onChange={onChange}
      startDate={startDate}
      endDate={endDate}
      formatWeekDay={day => day.slice(0, 3)}
      selectsRange
      dateFormat="dd/MM/YYYY"
      className="form-control"
      onKeyDown={e => e.preventDefault()}
    />
  );
}

const columns = [
  {
    accessorKey: 'date',
    header: 'Date',
    accessorFn: row => {
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(row.date));
    },
    meta: {
      headerProps: { className: 'white-space-nowrap text-900 ps-0' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10 ps-0' },
    },
  },
  {
    accessorKey: 'nfact',
    header: 'Numéro facture',
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'client',
    header: 'Client',
    meta: {
      headerProps: { className: 'text-start text-900 ' },
      cellProps: {
        className: 'ps-2 text-start w-5 text-primary fw-semibold fs-10',
      },
    },
  },
  {
    accessorKey: 'Type facture',
    header: 'Type',
    meta: {
      headerProps: {
        className: 'text-center text-900',
      },
      cellProps: {
        className: 'fs-9',
      },
    },
    cell: ({ row: { original } }) => {
      const { numfacture } = original;
      return (
        <SubtleBadge
          pill
          bg={numfacture !== '5' ? 'primary' : 'warning'}
          className="d-flex flex-center"
        >
          <p className="mb-0">{numfacture === '5' ? 'Retour' : 'Vente'}</p>
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'typeFacture',
    header: 'Type',
    meta: {
      headerProps: {
        className: 'text-center text-900',
      },
      cellProps: {
        className: 'fs-9',
      },
    },
    cell: ({ row: { original } }) => {
      const { typeFacture } = original;
      return (
        <SubtleBadge
          pill
          bg={classNames({
            primary: typeFacture === 'Retour',
            warning: typeFacture === 'Facture',
            secondary: typeFacture === null,
          })}
          className="d-flex flex-center fs-10"
        >
          <p className="mb-0">{typeFacture === null ? 'N/F' : typeFacture}</p>
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'magasin',
    header: 'Magasin',
    meta: {
      headerProps: { className: 'text-end text-900 w-2' },
      cellProps: { className: 'ps-2 text-end w-5 fs-10' },
    },
  },
];

const EntetesFactures = ({
  factures,
  loading,
  setCondition,
  setSelectedFactures,
  selectedFactures,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const { user } = useSelector(state => state.auth);
  const [currentCondition, setCurrentCondition] = useState({
    date: [
      new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      new Date(Date.now()).toISOString(),
    ],
    magasin: user.magasin,
    nfact: '',
    typeFacture: 'Tous',
  });

  const onDateChange = dates => {
    const [start, end] = dates;
    setCurrentCondition({
      ...currentCondition,
      date: [start, end],
    });
  };

  const table = useAdvanceTable({
    data: factures,
    columns: columns,
    selection: true,
    sortable: true,
    pagination: true,
    perPage: 8,
    selectionColumnWidth: 52,
  });

  const handleSubmit = e => {
    e.preventDefault();
    setCondition(currentCondition);
    setShowFilters(false);
  };

  const handleReset = async () => {
    const resetCondition = {
      date: [
        new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        new Date(Date.now()).toISOString(),
      ],
      magasin: 'Tous',
      nfact: '',
      typeFacture: 'Tous',
    };
    setCurrentCondition(resetCondition);
    setCondition(resetCondition);
    setSelectedFactures([]);
    setShowFilters(false);
  };

  useEffect(() => {
    const selectedRows = table.getSelectedRowModel().rows.map(row => row.original);
    setSelectedFactures(selectedRows);
  }, [table.getSelectedRowModel().rows, setSelectedFactures]);

  useEffect(() => {
    // Build the desired rowSelection object
    const newRowSelection = {};
    table.getRowModel().rows.forEach(row => {
      if (selectedFactures.some(facture => facture.nfact === row.original.nfact)) {
        newRowSelection[row.id] = true;
      }
    });

    // Only update if the selection is different
    const currentSelection = table.getState().rowSelection;
    const isSame =
      Object.keys(currentSelection).length === Object.keys(newRowSelection).length &&
      Object.keys(currentSelection).every(key => newRowSelection[key]);

    if (!isSame) {
      table.setRowSelection(newRowSelection);
    }
  }, [selectedFactures, table]);

  return (
    <Card className="h-100">
      <Card.Header style={{ borderBottom: '1px solid #e0e0e0' }}>
        <Card.Title className="d-flex justify-content-between align-items-center">
          <h5>Entêtes des factures</h5>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-primary" onClick={() => setShowFilters(v => !v)}>
              <IoFilter />
            </Button>
            <Button size="sm" variant="outline-danger" onClick={() => handleReset()}>
              <MdOutlineFilterListOff />
            </Button>
          </div>
        </Card.Title>
      </Card.Header>
      <Collapse in={showFilters}>
        <div className="p-3 border-bottom bg-light">
          <Form onSubmit={handleSubmit}>
            <div className="d-flex justify-content-between align-items-center">
              <Form.Group>
                <Form.Label>Date</Form.Label>
                <DatepickerFiltre
                  startDate={currentCondition.date[0]}
                  endDate={currentCondition.date[1]}
                  onChange={onDateChange}
                />
              </Form.Group>
              <Form.Group className="ms-3 w-50">
                <Form.Label>Magasin</Form.Label>
                <Form.Select
                  size="sm"
                  defaultValue={user.magasin}
                  onChange={e =>
                    setCurrentCondition({ ...currentCondition, magasin: e.target.value })
                  }
                  value={user.magasin === 'Tous' ? 'Tous' : user.magasin}
                  disabled={user.magasin !== 'Tous'}
                >
                  <option>Tous</option>
                  <option>EZZAHRA</option>
                  <option>BOUMHEL</option>
                  <option>SOUKRA</option>
                  <option>SFAX</option>
                  <option>MARSA</option>
                </Form.Select>
              </Form.Group>
            </div>
            <Form.Group className="mt-3">
              <Form.Label>Client</Form.Label>
              <Form.Control
                type="text"
                value={currentCondition.client}
                onChange={e => setCurrentCondition({ ...currentCondition, client: e.target.value })}
              />
            </Form.Group>
            <div className="d-flex justify-content-between align-items-center mt-3">
              <Form.Group>
                <Form.Label>Numéro Facture</Form.Label>
                <Form.Control
                  type="text"
                  value={currentCondition.nfact}
                  onChange={e =>
                    setCurrentCondition({ ...currentCondition, nfact: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="ms-3 w-50">
                <Form.Label>Type facture</Form.Label>
                <Form.Select
                  size="sm"
                  defaultValue={currentCondition.typeFacture}
                  onChange={e =>
                    setCurrentCondition({ ...currentCondition, typeFacture: e.target.value })
                  }
                  value={user.magasin === 'Tous' ? 'Tous' : 'Facture'}
                  disabled={user.magasin !== 'Tous'}
                >
                  <option>Tous</option>
                  <option>Facture</option>
                  <option>Retour</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="d-flex justify-content-end">
              <Button variant="primary" type="submit" className="mt-3">
                Rechercher
              </Button>
            </div>
          </Form>
        </div>
      </Collapse>
      <Card.Body>
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: 200 }}
          >
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <AdvanceTableProvider {...table}>
            <AdvanceTable />
            <AdvanceTableFooter rowInfo navButtons />
          </AdvanceTableProvider>
        )}
      </Card.Body>
    </Card>
  );
};

export default EntetesFactures;
